import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { Ripple } from "primereact/ripple";
import { Tooltip } from "primereact/tooltip";

import {
  AssignContact,
  TaskCarryForwardReason,
  Tasks,
} from "../../../Controllers/Types/Task/TaskInterface";
import {
  completeTaskWithEvidenceApi,
  createCarryForwardTaskApi,
  getCarryForwardReasonListApi,
  getUserValuableTaskSummaryApi,
  requestTaskFollowUpApi,
  taskPartUpdateApi,
} from "../../../Api/Task/TaskApi";
import { baseURL } from "../../../Api/ApiConstant";
import { formatDate } from "../../../Utils/getDate";

interface ToastMessage {
  severity: "success" | "error" | "warn" | "info";
  summary: string;
  detail: string;
}

interface CommonActionProps {
  visible: boolean;
  taskData: Tasks | null;
  onHide: () => void;
  onSuccess: (message: string) => void;
  showToast: (message: ToastMessage) => void;
}

interface TaskDetailsDialogProps {
  visible: boolean;
  taskData: Tasks | null;
  onHide: () => void;
  onEdit?: (task: Tasks) => void;
  canEdit?: boolean;
  canTaskCompleted?: boolean;
  onComplete: (task: Tasks) => void;
  onFollowUp: (task: Tasks) => void;
  onCarryForward: (task: Tasks) => void;
  onAssign: (task: Tasks) => void;
}

interface AssignTaskDialogProps extends CommonActionProps {
  contacts: AssignContact[];
}

const dialogHeader = (title: string) => {
  return (
    <div className="custom_modal_header_inner task_action_dialog_header">
      <h5 className="modal-title fs-5 text-white mb-0">{title}</h5>
    </div>
  );
};

/* -------------------- common helpers -------------------- */

export const normalizeTaskDate = (date?: string) => {
  if (!date) return "";

  const value = date.trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) return value;

  const yyyy = parsed.getFullYear();
  const mm = String(parsed.getMonth() + 1).padStart(2, "0");
  const dd = String(parsed.getDate()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd}`;
};

export const isAlreadyCarryForwardRequestedForCurrentDate = (
  taskData: Tasks | null
) => {
  if (!taskData) return false;

  const currentTaskDate = normalizeTaskDate(taskData.deu_date);

  if (!currentTaskDate) return false;

  const list: any[] =
    (taskData as any)?.carry_forward_data ??
    (taskData as any)?.carryForwardData ??
    (taskData as any)?.carry_forward ??
    (taskData as any)?.carry_forward_history ??
    [];

  return list.some((item) => {
    const taskDate = normalizeTaskDate(
      item?.task_date ?? item?.taskDate ?? item?.deu_date ?? ""
    );

    return taskDate === currentTaskDate;
  });
};

export const isTaskCompleted = (taskData: Tasks | null) => {
  return taskData?.is_completed === 1;
};

export const hasTaskFollowUp = (taskData: Tasks | null) => {
  const list: any[] =
    (taskData as any)?.followup_data ??
    (taskData as any)?.followUpData ??
    (taskData as any)?.follow_up ??
    (taskData as any)?.follow_up_history ??
    [];

  return list.length > 0;
};

export const isTaskHighPriority = (taskData: Tasks | null) => {
  return taskData?.priority === "High";
};

const viewDate = (dateString?: string) => {
  if (!dateString) return "---";

  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) return dateString;

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getPriorityColor = (priority?: string) => {
  if (priority === "High") return "#EF9A9A";
  if (priority === "Medium") return "#FFEFBC";
  if (priority === "Low") return "#B9F6CA";
  return "#FFEFBC";
};

const getStatusText = (taskData: Tasks | null) => {
  if (!taskData) return "---";

  if (isTaskCompleted(taskData)) return "Completed";

  if (isAlreadyCarryForwardRequestedForCurrentDate(taskData)) {
    return "Carried Forward";
  }

  if (hasTaskFollowUp(taskData)) return "Follow-up Requested";

  return "Ongoing";
};

const getStatusIcon = (taskData: Tasks | null) => {
  if (isTaskCompleted(taskData)) return "/assets/images/dashboard/done.svg";

  return "/assets/images/dashboard/in_progress.svg";
};

const getFileUrl = (filePath?: string) => {
  if (!filePath) return "";

  if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
    return filePath;
  }

  return `${baseURL}/${filePath.replace(/^\/+/, "")}`;
};

const isPdfFile = (filePath?: string) => {
  return filePath?.toLowerCase().endsWith(".pdf") ?? false;
};

const getTaskMinutes = (taskData: Tasks | null) => {
  return Number(taskData?.hours ?? 0) * 60 + Number(taskData?.minute ?? 0);
};

const getSelectedMinutes = (hours: number, minutes: number) => {
  return Number(hours ?? 0) * 60 + Number(minutes ?? 0);
};

const formatMinutesToHourText = (totalMinutes: number | null) => {
  if (totalMinutes === null) return "---";

  const hrs = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;

  return `${hrs.toString().padStart(2, "0")}:${mins
    .toString()
    .padStart(2, "0")}`;
};

/* -------------------- summary helpers -------------------- */

const parseTimeToMinutes = (value: any): number => {
  if (value === null || value === undefined || value === "") return 0;

  if (typeof value === "number") {
    return Math.round(value * 60);
  }

  const text = String(value).trim();

  if (!text) return 0;

  if (text.includes(":")) {
    const parts = text.split(":");
    const h = Number(parts[0] ?? 0);
    const m = Number(parts[1] ?? 0);

    if (Number.isNaN(h) || Number.isNaN(m)) return 0;

    return h * 60 + m;
  }

  const hourMatch = text.match(/(\d+(\.\d+)?)\s*(h|hr|hrs|hour)/i);
  const minMatch = text.match(/(\d+)\s*(m|min|mins|minute)/i);

  if (hourMatch || minMatch) {
    const h = hourMatch ? Number(hourMatch[1]) : 0;
    const m = minMatch ? Number(minMatch[1]) : 0;

    return Math.round(h * 60 + m);
  }

  const numeric = Number(text);

  if (Number.isNaN(numeric)) return 0;

  return Math.round(numeric * 60);
};

const getSummaryPayload = (summary: any) => {
  if (!summary) return null;

  if (summary?.data && typeof summary.data === "object") return summary.data;

  return summary;
};

const getDayTotalMinutes = (summary: any) => {
  const data = getSummaryPayload(summary);

  if (!data) return 8 * 60;

  const value =
    data?.dayWiseTotalWorkHours ??
    data?.day_wise_total_work_hours ??
    data?.day_total_work_hours ??
    data?.total_work_hours ??
    data?.totalHours ??
    data?.total_hours ??
    data?.working_hours ??
    data?.work_hours ??
    null;

  const minutes = parseTimeToMinutes(value);

  return minutes > 0 ? minutes : 8 * 60;
};

const getAlreadyAssignedMinutes = (summary: any) => {
  const data = getSummaryPayload(summary);

  if (!data) return 0;

  const value =
    data?.totalTaskHours ??
    data?.total_task_hours ??
    data?.assignedTaskHours ??
    data?.assigned_task_hours ??
    data?.usedTaskHours ??
    data?.used_task_hours ??
    data?.used_hours ??
    data?.assigned_hours ??
    null;

  const minutes = parseTimeToMinutes(value);

  return minutes < 0 ? 0 : minutes;
};

const getAvailableBeforeCurrentTask = (
  summary: any,
  oldMinutesToRemove: number = 0
) => {
  const total = getDayTotalMinutes(summary);
  let alreadyAssigned = getAlreadyAssignedMinutes(summary);

  if (oldMinutesToRemove > 0) {
    alreadyAssigned -= oldMinutesToRemove;
  }

  if (alreadyAssigned < 0) alreadyAssigned = 0;

  const available = total - alreadyAssigned;

  return available < 0 ? 0 : available;
};

const buildSummaryInput = (
  taskData: Tasks | null,
  contact: AssignContact | null
) => {
  if (!taskData || !contact) return null;

  return {
    id: contact.id,
    by: contact.by,
    date: taskData.deu_date,
  };
};

const SummaryBox: React.FC<{
  summaryLoading: boolean;
  summaryData: any;
  selectedMinutes?: number;
  oldMinutesToRemove?: number;
}> = ({
  summaryLoading,
  summaryData,
  selectedMinutes = 0,
  oldMinutesToRemove = 0,
}) => {
  if (summaryLoading) {
    return (
      <div className="task_summary_box">
        <div className="task_summary_header">
          <i className="bi bi-hourglass-split"></i>
          <span>Checking task summary...</span>
        </div>
      </div>
    );
  }

  if (!summaryData) return null;

  const data = getSummaryPayload(summaryData);

  const message =
    data?.message ??
    data?.massage ??
    summaryData?.message ??
    summaryData?.massage ??
    "";

  const totalDayMinutes = getDayTotalMinutes(summaryData);
  const alreadyAssignedMinutes = getAlreadyAssignedMinutes(summaryData);
  const availableMinutes = getAvailableBeforeCurrentTask(
    summaryData,
    oldMinutesToRemove
  );

  const isOver = selectedMinutes > availableMinutes;

  return (
    <div className={`task_summary_box ${isOver ? "has_warning" : ""}`}>
      <div className="task_summary_header">
        <i
          className={
            isOver ? "bi bi-exclamation-triangle" : "bi bi-clipboard2-data"
          }
        ></i>
        <span>{isOver ? "OT Warning" : "Task Summary"}</span>
      </div>

      {message && <p className="task_summary_message">{message}</p>}

      <div className="task_summary_grid">
        <div className="task_summary_item">
          <span>Day Total</span>
          <h6>{formatMinutesToHourText(totalDayMinutes)}</h6>
        </div>

        <div className="task_summary_item">
          <span>Assigned</span>
          <h6>{formatMinutesToHourText(alreadyAssignedMinutes)}</h6>
        </div>

        <div className="task_summary_item">
          <span>Available</span>
          <h6>{formatMinutesToHourText(availableMinutes)}</h6>
        </div>

        <div className="task_summary_item">
          <span>Selected</span>
          <h6>{formatMinutesToHourText(selectedMinutes)}</h6>
        </div>
      </div>

      {isOver && (
        <div className="task_summary_warning">
          <i className="bi bi-exclamation-circle"></i>
          <span>
            Only {formatMinutesToHourText(availableMinutes)} available for this
            user. Selected time is higher than available time.
          </span>
        </div>
      )}
    </div>
  );
};

const actionBlockedBox = (message: string) => {
  return (
    <div className="alert alert-warning mb-0 py-2 px-3">
      <small className="fw-semibold">{message}</small>
    </div>
  );
};

const contactAvatarList = (contacts: AssignContact[]) => {
  if (!contacts || contacts.length === 0) return <span>----</span>;

  return (
    <div className="project_card_contact_area mt-0 p-0 no_pointing">
      {contacts.map((contact, index) => {
        const contactTooltipId = `task_details_contact_${contact.id}_${index}`;

        return (
          <React.Fragment key={`${contact.id}_${index}`}>
            <Tooltip target={`#${contactTooltipId}`} />

            <div
              id={contactTooltipId}
              className="contact_profile"
              data-pr-tooltip={`${contact.first_name} ${contact.last_name}`}
              data-pr-position="top"
            >
              {contact.profile_image ? (
                <img
                  src={contact.profile_image}
                  alt={`${contact.first_name} ${contact.last_name}`}
                />
              ) : (
                <span>
                  {contact.first_name?.[0]}
                  {contact.last_name?.[0]}
                </span>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

const contactOptionsTemplate = (option: AssignContact) => {
  return (
    <div className="contact_option_group">
      <div className="contact_profile">
        {option?.profile_image ? (
          <img
            src={option.profile_image}
            alt={`${option.first_name} ${option.last_name}`}
          />
        ) : (
          <span>
            {option?.first_name?.[0]}
            {option?.last_name?.[0]}
          </span>
        )}
      </div>

      <div className="contact_details">
        <h6>
          {`${option?.first_name ?? ""} ${option?.last_name ?? ""}`.trim()}
        </h6>
        <small>{option?.role}</small>
      </div>
    </div>
  );
};

const selectedContactTemplate = (option: AssignContact, props: any) => {
  if (option) {
    return (
      <div className="d-flex align-items-center overflow-hidden">
        <span>
          {option.first_name} {option.last_name}
        </span>
      </div>
    );
  }

  return <span>{props.placeholder}</span>;
};

/* -------------------- details dialog -------------------- */

export const TaskDetailsDialog: React.FC<TaskDetailsDialogProps> = ({
  visible,
  taskData,
  onHide,
  onEdit,
  canEdit = false,
  canTaskCompleted = false,
  onComplete,
  onFollowUp,
  onCarryForward,
  onAssign,
}) => {
  const isCompleted = isTaskCompleted(taskData);
  const hasFollowUp = hasTaskFollowUp(taskData);
  const isHighPriority = isTaskHighPriority(taskData);
  const isAlreadyCarryForward =
    isAlreadyCarryForwardRequestedForCurrentDate(taskData);

  const canEditTask =
    !!taskData &&
    canEdit &&
    taskData.assign_by === null &&
    !isCompleted &&
    !isAlreadyCarryForward;

  const followupList: any[] =
    (taskData as any)?.followup_data ??
    (taskData as any)?.followUpData ??
    (taskData as any)?.follow_up ??
    [];

  const carryForwardList: any[] =
    (taskData as any)?.carry_forward_data ??
    (taskData as any)?.carryForwardData ??
    (taskData as any)?.carry_forward ??
    [];

  const totalTime = `${taskData?.hours ?? 0} Hour(s) ${
    taskData?.minute ?? 0
  } Minute(s)`;

  const header = (
    <div className="custom_modal_header_inner">
      <h5 className="modal-title fs-5 text-white mb-0">Task details</h5>

      <div className="d-flex align-items-center gap-2">
        {canEditTask && onEdit && taskData && (
          <button
            type="button"
            className="close_modal_btn p-ripple"
            onClick={() => onEdit(taskData)}
          >
            <i className="bi bi-pencil-square"></i>
            <Ripple />
          </button>
        )}

        <button
          type="button"
          className="close_modal_btn p-ripple"
          onClick={onHide}
        >
          <i className="bi bi-x-circle"></i>
          <Ripple />
        </button>
      </div>
    </div>
  );

  const renderActionButtons = () => {
    if (!canTaskCompleted || !taskData) return null;

    if (isCompleted) {
      return actionBlockedBox("This task is already completed.");
    }

    if (isAlreadyCarryForward) {
      return actionBlockedBox(
        "This task has already been carried forward. Actions are disabled."
      );
    }

    if (hasFollowUp) {
      return (
        <div className="task_details_action_area">
          {actionBlockedBox(
            "Follow-up already requested. Carry forward is disabled."
          )}

          <div className="task_details_action_row one_button">
            <Button
              label="Complete Task"
              icon="bi bi-check-circle"
              className="custom_btn primary task_details_action_btn"
              onClick={() => onComplete(taskData)}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="task_details_action_area">
        <div className="task_details_action_row">
          <Button
            label="Request Follow-up"
            icon="bi bi-calendar2-plus"
            className="custom_btn secondary task_details_action_btn"
            onClick={() => onFollowUp(taskData)}
          />

          <Button
            label="Complete Task"
            icon="bi bi-check-circle"
            className="custom_btn primary task_details_action_btn"
            onClick={() => onComplete(taskData)}
          />
        </div>

        {isHighPriority ? (
          actionBlockedBox("High priority task cannot be carried forward.")
        ) : (
          <div className="task_details_action_row one_button">
            <Button
              label="Carry Forward Task"
              icon="bi bi-forward"
              className="custom_btn secondary task_details_action_btn"
              onClick={() => onCarryForward(taskData)}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog
      visible={visible}
      header={header}
      headerClassName="custom_modal_header"
      className="custom_modal_dialog modal_dialog_md"
      onHide={onHide}
      dismissableMask
    >
      <div className="custom_modal_body">
        <div className="p-1">
          <div className="row">
            <div className="col-12">
              <h6 className="data_view_title text-capitalize">
                {taskData?.task_name || "---"}
              </h6>

              <div className="data_view_group mt-2">
                <h6 className="data_view_title text_gray text-capitalize">
                  {taskData?.project_reference || "---"}
                </h6>
                <span>|</span>
                <p className="data_view_detail">
                  Due date: {viewDate(taskData?.deu_date)}
                </p>
              </div>
            </div>
          </div>

          <hr className="custom_horizontal_line" />

          <div className="row">
            <div className="col-12">
              <h6 className="data_view_title">Description</h6>
              <p className="data_view_description mt-2">
                {taskData?.description || "----"}
              </p>
            </div>
          </div>

          <hr className="custom_horizontal_line" />

          <div className="row">
            <div className="col-3">
              <h6 className="data_view_title">Duration</h6>
            </div>
            <div className="col-9">
              <p className="data_view_detail mb-0">{totalTime}</p>
            </div>
          </div>

          <hr className="custom_horizontal_line" />

          <div className="row">
            <div className="col-3">
              <h6 className="data_view_title">Priority</h6>
            </div>
            <div className="col-9">
              <span
                className="priority_label"
                style={{ backgroundColor: getPriorityColor(taskData?.priority) }}
              >
                {taskData?.priority || "---"}
              </span>
            </div>
          </div>

          <hr className="custom_horizontal_line" />

          <div className="row">
            <div className="col-3">
              <h6 className="data_view_title">Assign</h6>
            </div>

            <div className="col-9 d-flex align-items-center gap-2 flex-wrap">
              {contactAvatarList(taskData?.assign ?? [])}

              {taskData &&
                taskData.assign_by === null &&
                !isCompleted &&
                !isAlreadyCarryForward && (
                  <button
                    type="button"
                    className="table_action_btn p-ripple"
                    onClick={() => onAssign(taskData)}
                  >
                    {taskData.assign?.length > 0 ? "Change Assign" : "Assign"}
                    <Ripple />
                  </button>
                )}
            </div>
          </div>

          <hr className="custom_horizontal_line" />

          <div className="row">
            <div className="col-3">
              <h6 className="data_view_title">Status</h6>
            </div>
            <div className="col-9">
              <div className="status_disply">
                <img src={getStatusIcon(taskData)} alt={getStatusText(taskData)} />
                <span>{getStatusText(taskData)}</span>
              </div>
            </div>
          </div>

          {followupList.length > 0 && (
            <>
              <hr className="custom_horizontal_line" />

              <div className="task_history_section">
                <h6 className="data_view_title mb-2">Follow-up History</h6>

                <div className="table-responsive">
                  <table className="task_history_table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Follow-up Date</th>
                        <th>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {followupList.map((item, index) => (
                        <tr key={`followup_${index}`}>
                          <td>{index + 1}</td>
                          <td>
                            {viewDate(
                              item.followup_date ??
                                item.followUpDate ??
                                item.created_at
                            )}
                          </td>
                          <td>{item.note || "No note"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {carryForwardList.length > 0 && (
            <>
              <hr className="custom_horizontal_line" />

              <div className="task_history_section">
                <h6 className="data_view_title mb-2">Carry Forward History</h6>

                <div className="table-responsive">
                  <table className="task_history_table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Task Date</th>
                        <th>Carry Forward Date</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {carryForwardList.map((item, index) => (
                        <tr key={`carry_${index}`}>
                          <td>{index + 1}</td>
                          <td>
                            {viewDate(
                              item.task_date ?? item.taskDate ?? taskData?.deu_date
                            )}
                          </td>
                          <td>
                            {viewDate(
                              item.carry_forward_date ??
                                item.carryForwardDate ??
                                item.forward_date ??
                                item.created_at
                            )}
                          </td>
                          <td>
                            {item.forward_reason?.task_carry_forward_reason ??
                              item.reason?.task_carry_forward_reason ??
                              item.task_carry_forward_reason ??
                              item.reason ??
                              item.note ??
                              "----"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          <hr className="custom_horizontal_line" />

          {renderActionButtons()}
        </div>
      </div>
    </Dialog>
  );
};

/* -------------------- completed evidence dialog -------------------- */

export const CompletedEvidenceDialog: React.FC<{
  visible: boolean;
  taskData: Tasks | null;
  onHide: () => void;
}> = ({ visible, taskData, onHide }) => {
  const proofs: any[] = (taskData as any)?.proof_data ?? [];

  const footer = (
    <div className="custom_modal_footer">
      <Button label="Close" className="custom_btn secondary" onClick={onHide} />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      header={dialogHeader("Completed Evidence")}
      footer={footer}
      className="custom_modal_dialog modal_dialog_md"
      headerClassName="custom_modal_header"
      onHide={onHide}
      dismissableMask
    >
      <div className="custom_modal_body">
        {proofs.length === 0 ? (
          <div className="task_empty_state">
            <i className="bi bi-file-earmark-x"></i>
            <span>No evidence found for this completed task.</span>
          </div>
        ) : (
          <div className="task_evidence_list">
            {proofs.map((proof, index) => {
              const filePath = proof.file_path ?? proof.file ?? "";
              const hasFile = filePath.trim() !== "";
              const fileUrl = getFileUrl(filePath);
              const isPdf = isPdfFile(filePath);

              return (
                <div className="task_evidence_card" key={`proof_${index}`}>
                  <div className="task_evidence_header">
                    <div className="task_evidence_icon">
                      <i
                        className={
                          hasFile
                            ? isPdf
                              ? "bi bi-file-earmark-pdf"
                              : "bi bi-file-earmark-image"
                            : "bi bi-sticky"
                        }
                      ></i>
                    </div>

                    <div>
                      <h6>Evidence {index + 1}</h6>
                      <small>
                        {proof.created_at ? viewDate(proof.created_at) : "---"}
                      </small>
                    </div>
                  </div>

                  {proof.note && (
                    <p className="task_evidence_note">{proof.note}</p>
                  )}

                  {hasFile && (
                    <button
                      type="button"
                      className="table_action_btn p-ripple"
                      onClick={() => window.open(fileUrl, "_blank")}
                    >
                      {isPdf ? "Open PDF" : "Open Image"}
                      <Ripple />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Dialog>
  );
};

/* -------------------- complete dialog -------------------- */

export const CompleteTaskDialog: React.FC<CommonActionProps> = ({
  visible,
  taskData,
  onHide,
  onSuccess,
  showToast,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [note, setNote] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [noteError, setNoteError] = useState("");
  const [fileError, setFileError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      setNote("");
      setFile(null);
      setNoteError("");
      setFileError("");
    }
  }, [visible]);

  const validate = () => {
    const hasNote = note.trim().length > 0;
    const hasFile = file !== null;

    if (!hasNote && !hasFile) {
      setNoteError("Please enter note or attach image/PDF evidence");
      setFileError("Evidence note or file is required");
      return false;
    }

    setNoteError("");
    setFileError("");
    return true;
  };

  const submit = async () => {
    if (!taskData) return;
    if (!validate()) return;

    setIsLoading(true);

    const response = await completeTaskWithEvidenceApi(
      taskData.task_id,
      note.trim(),
      file
    );

    setIsLoading(false);

    const success =
      response?.statusCode === 200 ||
      response?.status === true ;

    if (success) {
      onSuccess(response?.massage ??  "Task completed successfully.");
    } else {
      showToast({
        severity: "error",
        summary: "Error",
        detail: response?.massage ??  "There was an error completing the task.",
      });
    }
  };

  const footer = (
    <div className="custom_modal_footer">
      <Button
        label="Close"
        className="custom_btn secondary"
        onClick={onHide}
        disabled={isLoading}
      />

      <Button
        label={isLoading ? "Processing" : "Submit & Complete"}
        className="custom_btn primary"
        loading={isLoading}
        onClick={submit}
      />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      header={dialogHeader("Complete Task")}
      footer={footer}
      className="custom_modal_dialog modal_dialog_sm"
      headerClassName="custom_modal_header"
      onHide={onHide}
      dismissableMask
    >
      <div className="custom_modal_body">
        <div className="page_form_group">
          <label className="custom_form_label">Completion note</label>

          <InputTextarea
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              setNoteError("");
            }}
            rows={4}
            className="w-100"
            placeholder="Enter completion note"
          />

          {noteError && <small className="form_error_msg">{noteError}</small>}
        </div>

        <div className="page_form_group">
          <label className="custom_form_label">Evidence image / PDF</label>

          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.pdf"
            hidden
            onChange={(e) => {
              const selectedFile = e.target.files?.[0] ?? null;
              setFile(selectedFile);
              setFileError("");
            }}
          />

          <button
            type="button"
            className="add_data_btn p-ripple w-100 justify-content-center"
            onClick={() => fileInputRef.current?.click()}
          >
            <i className="bi bi-upload"></i>
            {file ? file.name : "Upload evidence"}
            <Ripple />
          </button>

          {fileError && <small className="form_error_msg">{fileError}</small>}
        </div>
      </div>
    </Dialog>
  );
};

/* -------------------- follow-up dialog -------------------- */

export const FollowUpDialog: React.FC<CommonActionProps> = ({
  visible,
  taskData,
  onHide,
  onSuccess,
  showToast,
}) => {
  const [note, setNote] = useState("");
  const [followUpDate, setFollowUpDate] = useState<Date | null>(null);
  const [noteError, setNoteError] = useState("");
  const [dateError, setDateError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      setNote("");
      setFollowUpDate(null);
      setNoteError("");
      setDateError("");
    }
  }, [visible]);

  const validate = () => {
    let isValid = true;

    if (!note.trim()) {
      setNoteError("Please enter follow-up note");
      isValid = false;
    } else {
      setNoteError("");
    }

    if (!followUpDate) {
      setDateError("Please select follow-up date");
      isValid = false;
    } else {
      setDateError("");
    }

    return isValid;
  };

  const submit = async () => {
    if (!taskData) return;
    if (!validate()) return;

    setIsLoading(true);

    const response = await requestTaskFollowUpApi(
      taskData.task_id,
      note.trim(),
      formatDate(followUpDate)
    );

    setIsLoading(false);

    const success =
      response?.statusCode === 200 ||
      response?.status === true ;

    if (success) {
      onSuccess(response?.massage ??  "Follow-up requested successfully.");
    } else {
      showToast({
        severity: "error",
        summary: "Error",
        detail: response?.massage ??  "There was an error requesting follow-up.",
      });
    }
  };

  const footer = (
    <div className="custom_modal_footer">
      <Button
        label="Close"
        className="custom_btn secondary"
        onClick={onHide}
        disabled={isLoading}
      />

      <Button
        label={isLoading ? "Processing" : "Submit Follow-up"}
        className="custom_btn primary"
        loading={isLoading}
        onClick={submit}
      />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      header={dialogHeader("Request Follow-up")}
      footer={footer}
      className="custom_modal_dialog modal_dialog_sm"
      headerClassName="custom_modal_header"
      onHide={onHide}
      dismissableMask
    >
      <div className="custom_modal_body">
        <div className="page_form_group">
          <label className="custom_form_label is_required">Follow-up date</label>

          <Calendar
            value={followUpDate}
            onChange={(e) => {
              setFollowUpDate(e.value as Date);
              setDateError("");
            }}
            dateFormat="dd M yy"
            minDate={new Date()}
            placeholder="Select follow-up date"
            className="custom_date_input w-100"
            showIcon
            readOnlyInput
          />

          {dateError && <small className="form_error_msg">{dateError}</small>}
        </div>

        <div className="page_form_group">
          <label className="custom_form_label is_required">Follow-up note</label>

          <InputTextarea
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              setNoteError("");
            }}
            rows={4}
            className="w-100"
            placeholder="Enter follow-up note"
          />

          {noteError && <small className="form_error_msg">{noteError}</small>}
        </div>
      </div>
    </Dialog>
  );
};

/* -------------------- carry forward dialog -------------------- */

export const CarryForwardDialog: React.FC<CommonActionProps> = ({
  visible,
  taskData,
  onHide,
  onSuccess,
  showToast,
}) => {
  const [reasonList, setReasonList] = useState<TaskCarryForwardReason[]>([]);
  const [selectedReason, setSelectedReason] =
    useState<TaskCarryForwardReason | null>(null);
  const [reasonError, setReasonError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [reasonLoading, setReasonLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      setSelectedReason(null);
      setReasonError("");
      fetchReasons();
    }
  }, [visible]);

  const fetchReasons = async () => {
    setReasonLoading(true);

    const response = await getCarryForwardReasonListApi();

    setReasonLoading(false);

    const success =
      response?.statusCode === 200 ||
      response?.status === true 
      ;

    if (success) {
      setReasonList(response?.data ?? []);
    } else {
      showToast({
        severity: "error",
        summary: "Error",
        detail: response?.massage ??  "Unable to load carry forward reasons.",
      });
    }
  };

  const submit = async () => {
    if (!taskData) return;

    if (!selectedReason) {
      setReasonError("Please select carry forward reason");
      return;
    }

    setIsLoading(true);

    const response = await createCarryForwardTaskApi(
      taskData.task_id,
      selectedReason.id
    );

    setIsLoading(false);

    const success =
      response?.statusCode === 200 ||
      response?.status === true 
    ;

    if (success) {
      onSuccess(response?.massage ??  "Carry forward requested successfully.");
    } else {
      showToast({
        severity: "error",
        summary: "Error",
        detail: response?.massage ?? "There was an error carrying forward task.",
      });
    }
  };

  const footer = (
    <div className="custom_modal_footer">
      <Button
        label="Close"
        className="custom_btn secondary"
        onClick={onHide}
        disabled={isLoading}
      />

      <Button
        label={isLoading ? "Processing" : "Submit Carry Forward"}
        className="custom_btn primary"
        loading={isLoading}
        onClick={submit}
      />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      header={dialogHeader("Carry Forward Task")}
      footer={footer}
      className="custom_modal_dialog modal_dialog_sm"
      headerClassName="custom_modal_header"
      onHide={onHide}
      dismissableMask
    >
      <div className="custom_modal_body">
        <div className="page_form_group">
          <label className="custom_form_label is_required">
            Carry forward reason
          </label>

          <Dropdown
            value={selectedReason}
            onChange={(e: DropdownChangeEvent) => {
              setSelectedReason(e.value);
              setReasonError("");
            }}
            options={reasonList}
            optionLabel="task_carry_forward_reason"
            placeholder={reasonLoading ? "Loading..." : "Select reason"}
            className="form_dropdown w-100"
            loading={reasonLoading}
            filter
            filterPlaceholder="Search reason"
            emptyMessage="No reason found"
          />

          {reasonError && (
            <small className="form_error_msg">{reasonError}</small>
          )}
        </div>
      </div>
    </Dialog>
  );
};

/* -------------------- assign dialog -------------------- */

export const AssignTaskDialog: React.FC<AssignTaskDialogProps> = ({
  visible,
  taskData,
  contacts,
  onHide,
  onSuccess,
  showToast,
}) => {
  const [selectedContact, setSelectedContact] = useState<AssignContact | null>(
    null
  );
  const [assignError, setAssignError] = useState("");
  const [timeError, setTimeError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryData, setSummaryData] = useState<any>(null);
  const [assignHr, setAssignHr] = useState<number>(0);
  const [assignMin, setAssignMin] = useState<number>(0);

  useEffect(() => {
    if (visible) {
      const currentContact = taskData?.assign?.[0] ?? null;

      setSelectedContact(currentContact);
      setAssignError("");
      setTimeError("");
      setSummaryData(null);
      setAssignHr(Number(taskData?.hours ?? 0));
      setAssignMin(Number(taskData?.minute ?? 0));

      if (currentContact) {
        fetchSummary(currentContact);
      }
    }
  }, [visible, taskData]);

  const fetchSummary = async (contact: AssignContact | null) => {
    if (!contact || !taskData) {
      setSummaryData(null);
      return;
    }

    const input = buildSummaryInput(taskData, contact);

    if (!input?.date) {
      setSummaryData(null);

      showToast({
        severity: "warn",
        summary: "Date required",
        detail: "Task due date is required to check summary.",
      });

      return;
    }

    setSummaryLoading(true);

    const response = await getUserValuableTaskSummaryApi(input);

    setSummaryLoading(false);

    const success =
      response?.statusCode === 200 ||
      response?.status === true 
      ;

    if (success) {
      setSummaryData(response?.data ?? response);
    } else {
      setSummaryData(null);

      showToast({
        severity: "error",
        summary: "Error",
        detail: response?.massage ??  "Unable to get task summary.",
      });
    }
  };

  const isSameOldAssign = () => {
    if (!taskData || !selectedContact) return false;

    const oldContact = taskData.assign?.[0];

    if (!oldContact) return false;

    return oldContact.id === selectedContact.id && oldContact.by === selectedContact.by;
  };

  const getOldMinutesToRemove = () => {
    if (isSameOldAssign()) return getTaskMinutes(taskData);

    return 0;
  };

  const validate = () => {
    let isValid = true;

    if (!selectedContact) {
      setAssignError("Please select contact");
      isValid = false;
    } else {
      setAssignError("");
    }

    const selectedMinutes = getSelectedMinutes(assignHr, assignMin);

    if (selectedMinutes <= 0) {
      setTimeError("Please enter task hours or minutes");
      isValid = false;
    } else {
      setTimeError("");
    }

    if (summaryLoading) {
      setTimeError("Please wait, checking available hours");
      isValid = false;
    }

    if (!summaryData && selectedContact) {
      setTimeError("User task summary not found");
      isValid = false;
    }

    if (summaryData) {
      const available = getAvailableBeforeCurrentTask(
        summaryData,
        getOldMinutesToRemove()
      );

      if (selectedMinutes > available) {
        setTimeError(`Only ${formatMinutesToHourText(available)} available for this user`);
        isValid = false;
      }
    }

    return isValid;
  };

  const submit = async () => {
    if (!taskData) return;
    if (!validate()) return;

    setIsLoading(true);

    const response = await taskPartUpdateApi(
      taskData.task_id,
      taskData.project_reference,
      taskData.task_name,
      assignHr,
      assignMin,
      taskData.description ?? "",
      taskData.deu_date,
      taskData.priority,
      [selectedContact!]
    );

    setIsLoading(false);

    const success =
      response?.statusCode === 200 ||
      response?.status === true 
     ;

    if (success) {
      onSuccess(response?.massage ??  "Task assigned successfully.");
    } else {
      showToast({
        severity: "error",
        summary: "Error",
        detail: response?.massage ??  "There was an error assigning task.",
      });
    }
  };

  const footer = (
    <div className="custom_modal_footer">
      <Button
        label="Close"
        className="custom_btn secondary"
        onClick={onHide}
        disabled={isLoading}
      />

      <Button
        label={isLoading ? "Processing" : "Assign Task"}
        className="custom_btn primary"
        loading={isLoading}
        onClick={submit}
      />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      header={dialogHeader("Assign Task")}
      footer={footer}
      className="custom_modal_dialog modal_dialog_md"
      headerClassName="custom_modal_header"
      onHide={onHide}
      dismissableMask
    >
      <div className="custom_modal_body">
        <div className="page_form_group">
          <label className="custom_form_label is_required">Assign contact</label>

          <Dropdown
            value={selectedContact}
            onChange={(e: DropdownChangeEvent) => {
              setSelectedContact(e.value);
              setAssignError("");
              setTimeError("");
              fetchSummary(e.value);
            }}
            options={contacts}
            optionLabel="first_name"
            itemTemplate={contactOptionsTemplate}
            valueTemplate={selectedContactTemplate}
            placeholder="Select contact"
            filter
            filterInputAutoFocus={true}
            filterPlaceholder="Search by name"
            filterBy="first_name,last_name,role"
            className="form_dropdown w-100"
            emptyFilterMessage="No contact(s) found!"
            showClear
          />

          {assignError && (
            <small className="form_error_msg">{assignError}</small>
          )}
        </div>

        <div className="row">
          <div className="col-12 col-sm-6">
            <div className="page_form_group">
              <label className="custom_form_label">Hr</label>
              <InputNumber
                value={assignHr}
                onValueChange={(e) => {
                  setAssignHr(Number(e.value ?? 0));
                  setTimeError("");
                }}
                min={0}
                max={23}
                className="w-100"
                inputClassName="w-100"
              />
            </div>
          </div>

          <div className="col-12 col-sm-6">
            <div className="page_form_group">
              <label className="custom_form_label">Min</label>
              <InputNumber
                value={assignMin}
                onValueChange={(e) => {
                  setAssignMin(Number(e.value ?? 0));
                  setTimeError("");
                }}
                min={0}
                max={59}
                className="w-100"
                inputClassName="w-100"
              />
            </div>
          </div>
        </div>

        <SummaryBox
          summaryLoading={summaryLoading}
          summaryData={summaryData}
          selectedMinutes={getSelectedMinutes(assignHr, assignMin)}
          oldMinutesToRemove={getOldMinutesToRemove()}
        />

        {timeError && <small className="form_error_msg">{timeError}</small>}
      </div>
    </Dialog>
  );
};